/**
 * Store Detail Page
 * 
 * Shows a single store with all its campaigns and tasks
 * Displays visit lifecycle controls
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import { useAuthUser } from '@/hooks/useAuthUser';
import { apiFetch } from '@/lib/api';
import { queryClient } from '@/main';
import { MobileScaffold } from '@/components/mobile/MobileScaffold';
import { CampaignCard } from '@/components/mobile/CampaignCard';
import { CampaignCardSkeleton } from '@/components/mobile/StoreFirstSkeletons';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, PlayCircle, PauseCircle, CheckCircle2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useStoreFirstError } from '@/hooks/useStoreFirstError';
import { useStoreVisit } from '@/hooks/useStoreVisit';
import { SignaturePad } from '@/components/mobile/SignaturePad';
import { TaskDetailModal } from '@/components/mobile/TaskDetailModal';
import { StoreFirstErrorBoundary } from '@/components/mobile/StoreFirstErrorBoundary';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { StoreVisit, PackTask, VisitSignature, CompletedTask, CampaignAtStore } from '@/types/store-first';
import { transformMobileTasksToStoreVisits } from '@/utils/transformMobileTasks';
import { useCompletion } from '@/hooks/useCompletion';
import { EvidenceViewModal } from '@/components/mobile/EvidenceViewModal';
import { ManagerSignatureModal } from '@/components/ManagerSignatureModal';
import { useStoreActions } from '@/hooks/useStoreActions';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ErrorState } from '@/components/ui/error-state';

function StoreDetailContent() {
  const [, setLocation] = useLocation();
  const [match, params] = useRoute('/store/:storeId');
  const { toast } = useToast();
  const { handleError } = useStoreFirstError();
  const { user } = useAuthUser();
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());
  const [selectedPackTask, setSelectedPackTask] = useState<PackTask | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignAtStore | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showManagerSignatureModal, setShowManagerSignatureModal] = useState(false);
  const [completedTasks, setCompletedTasks] = useState<CompletedTask[]>([]);
  const [showStartVisitDialog, setShowStartVisitDialog] = useState(false);
  
  // Proscriptive Workflow: Store actions
  const { startVisit, completeStore } = useStoreActions();

  // Get storeId from route params
  const storeId = params?.storeId ? parseInt(params.storeId) : null;

  const operatorId = user?.id ?? (user as any)?.userId ?? (user as any)?.localId;
  
  // ✅ CRITICAL: Get cycleId from URL params (passed from route-overview)
  // This ensures we use the CURRENT cycle, not old store_visit data
  const urlParams = new URLSearchParams(window.location.search);
  const cycleIdFromUrl = urlParams.get('cycleId') ? parseInt(urlParams.get('cycleId')!) : undefined;

  // Fetch completion data to check if store is complete
  const {
    data: completionData = [],
    isLoading: completionLoading
  } = useCompletion(cycleIdFromUrl);

  // Find completion data for this store
  const storeCompletion = useMemo(() => {
    if (!storeId || !completionData.length) return null;
    return completionData.find(c => c.storeId === storeId);
  }, [storeId, completionData]);

  const isStoreComplete = storeCompletion?.isComplete ?? false;

  // Check cache first before attempting fetch
  const cachedData = queryClient.getQueryData<{ visit: StoreVisit }>(['storeVisit', storeId, operatorId]);
  console.log('[STORE_DETAIL] Cached data check:', { 
    storeId, 
    operatorId, 
    hasCachedData: !!cachedData,
    cacheKey: ['storeVisit', storeId, operatorId]
  });

  // Fetch store visit data
  const {
    data: visitData,
    isLoading,
    error,
    refetch,
    isFetching
  } = useQuery<{ visit: StoreVisit }>({
    queryKey: ['storeVisit', storeId, operatorId],
    queryFn: async () => {
      if (!storeId || !operatorId || !cycleIdFromUrl) {
        throw new Error('Store ID, Operator ID, or Cycle ID not found');
      }

      console.log('[STORE_DETAIL] Fetching store visit data for store', storeId);
      const response = await apiFetch(`/api/mobile/tasks/${cycleIdFromUrl}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[STORE_DETAIL] API error:', { status: response.status, errorData });
        throw new Error(errorData.error || `Failed to fetch tasks: ${response.status}`);
      }

      const stores = await response.json();
      // Transform existing API response to store-first structure
      const storeVisits = transformMobileTasksToStoreVisits(stores);
      // Find the specific store visit
      const storeVisit = storeVisits.find(v => v.storeId === storeId);
      
      if (!storeVisit) {
        throw new Error('Store not found');
      }

      console.log('[STORE_DETAIL] Successfully loaded store visit data');
      return { visit: storeVisit };
    },
    enabled: !!storeId && !!operatorId && !!cycleIdFromUrl,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 24 * 60 * 60 * 1000, // 24 hours (keep in cache longer)
    refetchOnWindowFocus: false, // Use cache when offline
    refetchOnReconnect: true, // Only refetch when back online
    refetchOnMount: false, // Use cache first
    retry: false, // Don't retry when offline - use cache instead
    initialData: cachedData, // Use cached data if available
    initialDataUpdatedAt: cachedData ? Date.now() : undefined, // Mark as fresh if we have cached data
  });

  console.log('[STORE_DETAIL] Query state:', { 
    hasData: !!visitData, 
    isLoading, 
    isFetching, 
    hasError: !!error,
    errorMessage: error instanceof Error ? error.message : null
  });

  const visit = visitData?.visit;
  const { startVisit: startVisitOld, pauseVisit, completeVisit } = useStoreVisit(storeId || 0, visit?.cycleId || undefined);

  // Toggle campaign expansion
  const toggleCampaign = (campaignId: number) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  // Handle pack task click - opens pack completion modal OR evidence view
  const handlePackTaskClick = (packTask: PackTask, campaign: CampaignAtStore) => {
    // If completed in ANY way, show evidence view (READ-ONLY) instead of completion modal
    const isCompleted = packTask.status === 'completed' || 
                       packTask.status === 'issue' || 
                       packTask.status === 'operator_complete' || 
                       packTask.status === 'admin_complete' ||
                       packTask.status === 'locked';
    
    if (isCompleted) {
      console.log('[STORE_DETAIL] Opening evidence view for completed assignment', {
        assignmentId: packTask.assignmentId,
        status: packTask.status
      });
      setSelectedPackTask(packTask);
      setSelectedCampaign(campaign);
      setShowEvidenceModal(true);
      setShowTaskModal(false); // Ensure completion modal is closed
      return;
    }
    
    // Otherwise, allow task completion
    setSelectedPackTask(packTask);
    setSelectedCampaign(campaign);
    setShowTaskModal(true);
    setShowEvidenceModal(false); // Ensure evidence modal is closed
  };

  // Handle pack completion - updates pack task
  const handleTaskComplete = async (completedTask: CompletedTask) => {
    if (!selectedCampaign || !selectedPackTask) return;

    // Add to completed tasks list (pack-level completion)
    setCompletedTasks(prev => {
      // Remove if already exists (update scenario)
      const filtered = prev.filter(t => t.assignmentId !== completedTask.assignmentId);
      return [...filtered, completedTask];
    });

    // Update local visit state optimistically
    if (visit) {
      const updatedVisit = { ...visit };
      
      // Update pack task in the completed campaign
      updatedVisit.campaigns = visit.campaigns.map(campaign => {
        if (campaign.campaignId === selectedCampaign.campaignId) {
          return {
            ...campaign,
            packTask: campaign.packTask ? {
              ...campaign.packTask,
              status: completedTask.status as any,
              deliveredQuantity: completedTask.deliveredQuantity,
            } : campaign.packTask,
            status: completedTask.status === 'completed' ? 'completed' : 'issue',
          };
        }
        return campaign;
      });

      // Refetch to get latest state from server
      refetch();
    }

    toast({
      title: 'Pack Completed',
      description: `${selectedCampaign.packName || selectedCampaign.campaignName} has been marked as ${completedTask.status}`,
    });
  };

  // Proscriptive Workflow: Handle visit start with backend validation
  const handleStartVisit = async () => {
    if (!operatorId || !cycleIdFromUrl) {
      toast({
        title: 'Error',
        description: 'Missing operator ID or cycle ID',
        variant: 'destructive',
      });
      return;
    }

    startVisit.mutate({
      storeId: storeId!,
      operatorId: Number(operatorId),
      cycleId: cycleIdFromUrl,
    }, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  // Handle visit pause
  const handlePauseVisit = async () => {
    try {
      const progress = {
        currentCampaignId: visit?.campaigns[0]?.campaignId || null,
        currentTaskId: null,
        completedTasks: completedTasks,
      };
      await pauseVisit(progress);
      toast({
        title: 'Visit Paused',
        description: 'You can resume later',
      });
      refetch();
    } catch (err) {
      handleError(err, 'Pause Visit');
    }
  };

  // Handle visit completion
  const handleCompleteVisit = async (signature: VisitSignature) => {
    try {
      await completeVisit(signature, completedTasks);
      toast({
        title: 'Visit Completed',
        description: 'All tasks have been submitted',
      });
      // Navigate back to route overview
      setTimeout(() => {
        setLocation('/route-overview');
      }, 2000);
    } catch (err) {
      handleError(err, 'Complete Visit');
    }
  };

  // Check if all pack tasks are completed
  const allTasksCompleted = useMemo(() => {
    if (!visit) return false;
    return visit.campaigns.every(campaign =>
      campaign.packTask && (campaign.packTask.status === 'completed' || campaign.packTask.status === 'issue')
    );
  }, [visit]);

  if (!storeId) {
    return (
      <MobileScaffold title="Store Not Found" showBack onBackClick={() => setLocation('/route-overview')}>
        <div className="p-4">
          <p className="text-gray-600">Invalid store ID</p>
        </div>
      </MobileScaffold>
    );
  }

  // Show error state only if we don't have cached data
  if (error && !isLoading && !visitData) {
    return (
      <MobileScaffold 
        title="Error Loading Store"
        showBack
        onBackClick={() => setLocation('/route-overview')}
      >
        <div className="p-4">
          <ErrorState
            message={error instanceof Error ? error.message : 'An unexpected error occurred'}
            onRetry={() => refetch()}
            className="min-h-[300px]"
          />
        </div>
      </MobileScaffold>
    );
  }

  return (
    <MobileScaffold 
      title={visit?.storeName || 'Store Details'}
      showBack
      onBackClick={() => setLocation('/route-overview')}
    >
      <div className="p-4 space-y-4">
        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border p-4 mb-4">
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-64 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <CampaignCardSkeleton key={i} />
              ))}
            </div>
          </div>
        )}

        {/* Store Information */}
        {visit && !isLoading && (
          <>
            {/* Store Header */}
            <div className="bg-white rounded-lg border p-4 mb-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h2 className="text-xl font-bold mb-1">{visit.storeName}</h2>
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                    <MapPin className="h-4 w-4" />
                    <span>{visit.storeAddress}</span>
                  </div>
                  {visit.retailerName && (
                    <div className="text-sm text-gray-600">
                      {visit.retailerName}
                    </div>
                  )}
                </div>
              </div>

              {/* Visit Status Badge */}
              <div className="flex items-center gap-2 mt-3">
                <StatusBadge status={isStoreComplete ? 'completed' : visit.visitStatus} />
              </div>
            </div>

            {/* Proscriptive Workflow: Visit Controls */}
            {/* Show start visit button for not_started/pending stores */}
            {(visit.visitStatus === 'pending' || visit.visitStatus === 'not_started') && !isStoreComplete && (
              <Button
                onClick={() => setShowStartVisitDialog(true)}
                className="w-full mb-4"
                size="lg"
                disabled={startVisit.isPending}
              >
                {startVisit.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-5 w-5 mr-2" />
                    Start Visit
                  </>
                )}
              </Button>
            )}

            {/* Show completion message if store is complete (posting done + signature) */}
            {isStoreComplete && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-800">✅ Store Fully Complete</h3>
                    <p className="text-sm text-green-700">
                      All tasks complete AND manager signature obtained. Store is closed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show posting complete but awaiting signature message */}
            {!isStoreComplete &&
              visit.visitStatus !== 'signed_off' &&
              storeCompletion?.postingComplete &&
              !storeCompletion?.isSigned && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-amber-600" />
                  <div>
                    <h3 className="font-semibold text-amber-800">🔏 Awaiting Signature</h3>
                    <p className="text-sm text-amber-700">
                      All tasks complete. Manager signature required to finish this store.
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <Button
                    onClick={() => setShowManagerSignatureModal(true)}
                    className="w-full"
                    size="lg"
                  >
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Capture Manager Signature
                  </Button>
                </div>
              </div>
            )}

            {/* Proscriptive Workflow: Mark Complete Button */}
            {visit.visitStatus === 'in_progress' && allTasksCompleted && (
              <div className="sticky bottom-0 p-4 bg-white border-t shadow-lg mb-4">
                <Button
                  onClick={() => {
                    if (!operatorId || !cycleIdFromUrl) {
                      toast({
                        title: 'Error',
                        description: 'Missing operator ID or cycle ID',
                        variant: 'destructive',
                      });
                      return;
                    }
                    completeStore.mutate({
                      storeId: storeId!,
                      operatorId: Number(operatorId),
                      cycleId: cycleIdFromUrl,
                    }, {
                      onSuccess: () => {
                        refetch();
                        toast({
                          title: 'Store marked complete',
                          description: 'Ready for manager signature',
                        });
                      },
                    });
                  }}
                  disabled={completeStore.isPending}
                  className="w-full"
                  size="lg"
                >
                  {completeStore.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Marking Complete...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Mark Store Complete
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Campaigns List */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg mb-2">Campaigns</h3>
              {visit.campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.campaignId}
                  campaign={campaign}
                  isExpanded={expandedCampaigns.has(campaign.campaignId)}
                  onToggle={() => toggleCampaign(campaign.campaignId)}
                  onPackTaskClick={(packTask) => handlePackTaskClick(packTask, campaign)}
                />
              ))}
            </div>
          </>
        )}

        {/* Task Detail Modal */}
        {storeId && selectedCampaign && (
          <TaskDetailModal
            packTask={selectedPackTask}
            campaign={selectedCampaign}
            storeId={storeId}
            storeName={visit?.storeName}
            isOpen={showTaskModal}
            onClose={() => {
              setShowTaskModal(false);
              setSelectedPackTask(null);
              setSelectedCampaign(null);
            }}
            onComplete={handleTaskComplete}
            onStoreReadyForSignature={() => {
              // ✅ PROSCRIPTIVE WORKFLOW: Removed automatic signature trigger
              // Signature should only open via explicit button click
              // This callback is kept for compatibility but does nothing
            }}
          />
        )}

        {/* Evidence View Modal */}
        {selectedPackTask && selectedCampaign && (
          <EvidenceViewModal
            packTask={selectedPackTask}
            campaign={selectedCampaign}
            isOpen={showEvidenceModal}
            onClose={() => {
              setShowEvidenceModal(false);
              setSelectedPackTask(null);
              setSelectedCampaign(null);
            }}
          />
        )}

        {/* Signature Pad Dialog */}
        <Dialog open={showSignaturePad} onOpenChange={setShowSignaturePad}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Store Manager Signature</DialogTitle>
            </DialogHeader>
            <SignaturePad
              onSignature={(signatureData) => {
                const signature: VisitSignature = {
                  signedBy: 'Store Manager', // TODO: Get from input field
                  signedAt: new Date(),
                  signatureData: signatureData,
                };
                handleCompleteVisit(signature);
                setShowSignaturePad(false);
              }}
              onCancel={() => setShowSignaturePad(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Manager Signature Modal (direct sign-off path) */}
        <ManagerSignatureModal
          open={showManagerSignatureModal}
          onClose={() => {
            setShowManagerSignatureModal(false);
            // Refresh completion/signoff state after closing
            refetch();
          }}
          storeId={storeId}
          storeName={visit?.storeName || null}
          operatorId={operatorId}
          cycleId={cycleIdFromUrl ?? visit?.cycleId ?? undefined} // ✅ Use URL cycleId first (current cycle)
        />

        {/* Proscriptive Workflow: Start Visit Confirmation Dialog */}
        <AlertDialog open={showStartVisitDialog} onOpenChange={setShowStartVisitDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Start Store Visit?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark your visit to {visit?.storeName} as in progress.
                You can only work on one store at a time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleStartVisit}>
                Start Visit
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MobileScaffold>
  );
}

function StatusBadge({ status }: { status: StoreVisit['visitStatus'] }) {
  const config: Record<string, { color: string; label: string }> = {
    not_started: { color: 'bg-gray-100 text-gray-700', label: 'Ready to Start' },
    pending: { color: 'bg-gray-100 text-gray-700', label: 'Pending' },
    in_progress: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
    paused: { color: 'bg-orange-100 text-orange-700', label: 'Paused' },
    completed: { color: 'bg-green-100 text-green-700', label: 'Ready for Signature' },
    signed_off: { color: 'bg-green-100 text-green-700', label: 'Signed Off' },
    reopened: { color: 'bg-yellow-100 text-yellow-700', label: 'Reopened' },
  };

  const { color, label } = config[status] || { color: 'bg-gray-100 text-gray-700', label: 'Unknown' };

  return (
    <Badge className={`${color} px-3 py-1 rounded-full text-sm font-medium`}>
      {label}
    </Badge>
  );
}

export default function StoreDetailPage() {
  return (
    <StoreFirstErrorBoundary>
      <StoreDetailContent />
    </StoreFirstErrorBoundary>
  );
}
